// HistoryPage — ajaloo vaade graafikutega.
// Vali park + ajavahemik → backend tagastab snapshot rea snapshot tabelist (5min sammud)
// ning recharts joonistab.

import { useState, useEffect, useMemo } from 'react';
import { Header } from '../components/Layout';
import { useParks } from '../contexts/ParksContext';
import { EleringChart } from '../components/EleringChart';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  ResponsiveContainer, AreaChart, Area,
} from 'recharts';

const PERIODS = [
  { id: '1h',  label: '1 h',  group: 'rolling' },
  { id: '6h',  label: '6 h',  group: 'rolling' },
  { id: '24h', label: '24 h', group: 'rolling' },
  { id: '7d',  label: '7 päeva',  group: 'rolling' },
  { id: '30d', label: '30 päeva', group: 'rolling' },
  { id: 'today',      label: 'Täna',      group: 'calendar' },
  { id: 'yesterday',  label: 'Eile',      group: 'calendar' },
  { id: 'this_week',  label: 'See nädal', group: 'calendar' },
  { id: 'this_month', label: 'See kuu',   group: 'calendar' },
  { id: 'this_year',  label: 'See aasta', group: 'calendar' },
  { id: 'custom',     label: '📅 Vali ajavahemik', group: 'custom' },
];

function computeRange(period, customFrom, customTo) {
  const now = Date.now();
  if (period === 'custom') {
    if (customFrom && customTo) {
      return { from: new Date(customFrom).getTime(), to: new Date(customTo).getTime() };
    }
    return null;
  }
  if (['today','yesterday','this_week','this_month','this_year'].includes(period)) {
    const d = new Date();
    const startOfDay = new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
    if (period === 'today')      return { from: startOfDay, to: now };
    if (period === 'yesterday')  return { from: startOfDay - 86400e3, to: startOfDay - 1 };
    if (period === 'this_week')  { const dow = (d.getDay() + 6) % 7; return { from: startOfDay - dow*86400e3, to: now }; }
    if (period === 'this_month') return { from: new Date(d.getFullYear(), d.getMonth(), 1).getTime(), to: now };
    if (period === 'this_year')  return { from: new Date(d.getFullYear(), 0, 1).getTime(), to: now };
  }
  return null;
}

const METRICS = [
  { id: 'solar_p',     label: 'PEJ võimsus',       color: '#ca8a04', unit: 'kW' },
  { id: 'grid_p',      label: 'Võrk võimsus',      color: '#1d4ed8', unit: 'kW' },
  { id: 'bau_meter_p', label: 'Aku AC võimsus',    color: '#6d28d9', unit: 'kW' },
  { id: 'bau_soc',     label: 'Aku SOC',           color: '#16a34a', unit: '%' },
  { id: 'bau_chg_today', label: 'Aku laadimine täna', color: '#15803d', unit: 'kWh' },
  { id: 'bau_dsg_today', label: 'Aku tühjenemine täna', color: '#b45309', unit: 'kWh' },
];

function fmt(v, decimals = 2) {
  if (v === null || v === undefined || Number.isNaN(v)) return '—';
  return v.toLocaleString('et-EE', { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
}

function fmtTime(ts, period) {
  const d = new Date(ts);
  if (period === '1h' || period === '6h' || period === '24h') {
    return d.toLocaleTimeString('et-EE', { hour: '2-digit', minute: '2-digit' });
  }
  if (period === '7d' || period === '30d') {
    return d.toLocaleDateString('et-EE', { month: 'short', day: 'numeric' }) + ' ' +
           d.toLocaleTimeString('et-EE', { hour: '2-digit', minute: '2-digit' });
  }
  return d.toLocaleDateString('et-EE', { month: 'short', day: 'numeric' });
}

export function HistoryPage({ wsConnected }) {
  const { data: parksData } = useParks();
  const onlineParks = useMemo(() => {
    return (parksData?.parks || []).filter(p => p.name).map(p => ({
      slot: p.slot, name: p.name, online: p.online,
    }));
  }, [parksData]);

  // Mitu parki korraga; 'all' tähendab kõik agregaadina (1 päring kogu summa kohta)
  const [selectedSlots, setSelectedSlots] = useState(['all']);
  const [period, setPeriod] = useState('24h');
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');
  const [selectedMetrics, setSelectedMetrics] = useState([]);  // tühi — kasutaja vajutab
  // Per-slot snapshotid: { slot|'all': [snaps] }
  const [snapshotsBySlot, setSnapshotsBySlot] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [updated, setUpdated] = useState(null);

  const isAll = selectedSlots.includes('all') || selectedSlots.length === 0;

  // Lae ajalugu — kui 'all' on valitud, küsime kogu agregaati;
  // muidu paralleelselt iga sloti kohta + summeerime kohapeal.
  useEffect(() => {
    let cancel = false;
    async function load() {
      setLoading(true);
      setError(null);
      const range = computeRange(period, customFrom, customTo);
      const qs = range ? `from=${range.from}&to=${range.to}` : `period=${period}`;
      try {
        if (isAll) {
          const res = await fetch(`/api/parks/history?${qs}`);
          if (!res.ok) throw new Error(`HTTP ${res.status}`);
          const json = await res.json();
          if (cancel) return;
          setSnapshotsBySlot({ all: json.snapshots || [] });
        } else {
          const result = {};
          await Promise.all(selectedSlots.filter(s => s !== 'all').map(async (slot) => {
            const r = await fetch(`/api/parks/${slot}/history?${qs}`);
            if (r.ok) {
              const j = await r.json();
              result[slot] = j.snapshots || [];
            }
          }));
          if (cancel) return;
          setSnapshotsBySlot(result);
        }
        setUpdated(Date.now());
      } catch (err) {
        if (!cancel) setError(err.message);
      } finally {
        if (!cancel) setLoading(false);
      }
    }
    load();
    return () => { cancel = true; };
  }, [selectedSlots, period, customFrom, customTo, isAll]);

  // Tagasi-ühilduvuseks: snapshots = kõikide valitud parkide kombineeritud rida (ts → agregeeritud)
  const snapshots = useMemo(() => {
    if (isAll) return snapshotsBySlot.all || [];
    const byTs = new Map();
    for (const [slot, arr] of Object.entries(snapshotsBySlot)) {
      for (const s of arr) {
        if (!byTs.has(s.ts)) byTs.set(s.ts, { ts: s.ts });
        const r = byTs.get(s.ts);
        // summeeritud — energia ja võimsus liidetakse, SOC keskmistatakse
        for (const k of ['solar_p','grid_p','bau_meter_p','solar_e_fwd','grid_e_rev','grid_e_fwd','bau_chg_today','bau_dsg_today','bau_meter_e_fwd']) {
          if (s[k] != null) r[k] = (r[k] || 0) + s[k];
        }
        if (s.bau_soc != null) {
          r._soc_sum = (r._soc_sum || 0) + s.bau_soc;
          r._soc_count = (r._soc_count || 0) + 1;
        }
      }
    }
    const arr = [...byTs.values()].sort((a, b) => a.ts - b.ts);
    for (const r of arr) {
      if (r._soc_count) r.bau_soc = r._soc_sum / r._soc_count;
      delete r._soc_sum; delete r._soc_count;
    }
    return arr;
  }, [snapshotsBySlot, isAll]);

  const toggleSlot = (slot) => {
    setSelectedSlots(prev => {
      if (slot === 'all') return ['all'];
      const without = prev.filter(s => s !== 'all');
      return without.includes(slot) ? without.filter(s => s !== slot) : [...without, slot];
    });
  };

  // Andmete teisendus recharts'ile
  const chartData = useMemo(() => {
    return snapshots.map((s) => ({
      ts: s.ts,
      time: fmtTime(s.ts, period),
      solar_p: s.solar_p,
      grid_p: s.grid_p,
      bau_meter_p: s.bau_meter_p,
      bau_soc: s.bau_soc,
      bau_chg_today: s.bau_chg_today,
      bau_dsg_today: s.bau_dsg_today,
      solar_e_fwd: s.solar_e_fwd,
      grid_e_rev: s.grid_e_rev,
      grid_e_fwd: s.grid_e_fwd,
    }));
  }, [snapshots, period]);

  const visibleMetrics = METRICS.filter(m => selectedMetrics.includes(m.id));

  // Perioodi energia kokkuvõte — cumulative loendurite delta (esimene → viimane snapshot)
  const summary = useMemo(() => {
    if (snapshots.length < 2) return null;
    const first = snapshots[0];
    const last = snapshots[snapshots.length - 1];
    const safeDelta = (a, b) => {
      if (a == null || b == null) return null;
      const d = a - b;
      return d >= 0 ? d : null;
    };

    const pej_kwh          = safeDelta(last.solar_e_fwd, first.solar_e_fwd);
    const voork_export_kwh = safeDelta(last.grid_e_rev,  first.grid_e_rev);
    const voork_import_kwh = safeDelta(last.grid_e_fwd,  first.grid_e_fwd);
    const bau_ac_kwh       = safeDelta(last.bau_meter_e_fwd, first.bau_meter_e_fwd);
    const duration_h       = (last.ts - first.ts) / 3600e3;

    // Tähendusrikkad tuletised:
    // Omatarbimine = PEJ toodetud − võrku eksporditud (kasutati kohapeal)
    const omatarbimine = (pej_kwh != null && voork_export_kwh != null)
      ? Math.max(0, pej_kwh - voork_export_kwh) : null;
    // Omatarbimise %
    const omatarbimine_pct = (pej_kwh != null && pej_kwh > 0 && omatarbimine != null)
      ? (omatarbimine / pej_kwh * 100) : null;
    // Netoenergia võrgust = import − eksport (positiivne = võrgust võetud)
    const neto_voork = (voork_import_kwh != null && voork_export_kwh != null)
      ? voork_import_kwh - voork_export_kwh : null;
    // SOC min/max/avg perioodi jooksul + delta (kui palju SOC muutus)
    const socs = snapshots.map(s => s.bau_soc).filter(v => v != null);
    const soc_min = socs.length ? Math.min(...socs) : null;
    const soc_max = socs.length ? Math.max(...socs) : null;
    const soc_avg = socs.length ? socs.reduce((a, b) => a + b, 0) / socs.length : null;
    const soc_delta = (first.bau_soc != null && last.bau_soc != null)
      ? last.bau_soc - first.bau_soc : null;
    // Aku läbilaskvus = täna laetud + tühjendatud (tsükli komponendid)
    const bau_chg_delta = safeDelta(last.bau_chg_today, first.bau_chg_today);
    const bau_dsg_delta = safeDelta(last.bau_dsg_today, first.bau_dsg_today);
    const bau_throughput = (bau_chg_delta || 0) + (bau_dsg_delta || 0);
    // Round-trip kasutus = (chg + dsg) / 2 võrreldes mahuvuse ekvivalendiga
    // (puhtalt indikatiivne, kasutame fikseerd 261 kWh kapaciteet × parkide arv)
    const cap_total_kwh = isAll
      ? (parksData?.parks || []).filter(p => p.name).length * 261
      : selectedSlots.filter(s => s !== 'all').length * 261;
    const cycles = (cap_total_kwh > 0) ? bau_throughput / (2 * cap_total_kwh) : null;
    // Tipuvõimsus (max p_total üle perioodi)
    const pvs = snapshots.map(s => s.solar_p).filter(v => v != null);
    const pej_peak_kw = pvs.length ? Math.max(...pvs) : null;
    const gridPs = snapshots.map(s => s.grid_p).filter(v => v != null);
    const grid_peak_export_kw = gridPs.length ? Math.abs(Math.min(0, ...gridPs)) : null;  // negatiivne = export
    const grid_peak_import_kw = gridPs.length ? Math.max(0, ...gridPs) : null;
    // PEJ keskmine võimsus
    const pej_avg_kw = pvs.length ? pvs.reduce((a, b) => a + b, 0) / pvs.length : null;
    // Capacity factor (PEJ avg / installed) — Goodwe 50 kW × parkide arv
    const installed_kw = isAll
      ? (parksData?.parks || []).filter(p => p.name).reduce((acc, p) => acc + (p.inverters || []).reduce((s, i) => s + (i.type?.includes('60KTL') ? 60 : i.type?.includes('33KTL') ? 33 : i.type?.includes('20KTL') ? 20 : 50), 0), 0)
      : selectedSlots.filter(s => s !== 'all').reduce((acc, slot) => {
          const p = (parksData?.parks || []).find(x => x.slot === slot);
          return acc + ((p?.inverters || []).reduce((s, i) => s + (i.type?.includes('60KTL') ? 60 : i.type?.includes('33KTL') ? 33 : i.type?.includes('20KTL') ? 20 : 50), 0));
        }, 0);
    const cap_factor = (pej_avg_kw != null && installed_kw > 0) ? (pej_avg_kw / installed_kw * 100) : null;

    return {
      duration_h, first_ts: first.ts, last_ts: last.ts,
      pej_kwh, voork_export_kwh, voork_import_kwh, bau_ac_kwh,
      omatarbimine, omatarbimine_pct, neto_voork,
      soc_min, soc_max, soc_avg, soc_delta,
      bau_chg_delta, bau_dsg_delta, bau_throughput, cycles,
      pej_peak_kw, pej_avg_kw, grid_peak_export_kw, grid_peak_import_kw,
      installed_kw, cap_factor,
    };
  }, [snapshots, isAll, selectedSlots, parksData]);

  const toggleMetric = (id) => {
    setSelectedMetrics((prev) =>
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
    );
  };

  const selectedPark = isAll
    ? { name: 'Kõik pargid kokku' }
    : (selectedSlots.length === 1
        ? onlineParks.find(p => p.slot === selectedSlots[0])
        : { name: `${selectedSlots.length} parki kokku` });

  return (
    <div className="h-full flex flex-col" style={{ background: 'var(--bg)' }}>
      <Header
        title="Ajalugu"
        connected={wsConnected}
        lastUpdate={updated}
      />

      <div style={{ flex: 1, overflow: 'auto', padding: '20px 28px 40px', maxWidth: 1480, width: '100%', margin: '0 auto' }}>
        {/* Elering-stiilis ELEKTER tulpgraafik — GRID energia mõõtepunktide kaupa */}
        <EleringChart />

        {/* Filtrid */}
        <div style={{
          display: 'flex', gap: 16, flexWrap: 'wrap', marginBottom: 20,
          background: 'var(--surface)', border: '1px solid var(--border)',
          borderRadius: 6, padding: '14px 16px',
        }}>
          {/* Park valik — multi-select */}
          <div style={{ flex: '1 1 100%' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
              <div style={{ fontSize: '0.62rem', color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                Pargid
              </div>
              <div style={{ display: 'flex', gap: 4 }}>
                <button
                  onClick={() => setSelectedSlots(['all'])}
                  style={{ padding: '3px 8px', borderRadius: 3, border: '1px solid var(--border)', background: isAll ? '#18181b' : 'white', color: isAll ? 'white' : 'var(--text-2)', fontSize: '0.66rem', cursor: 'pointer' }}
                >
                  ⊕ Kõik kokku
                </button>
                <button
                  onClick={() => setSelectedSlots([])}
                  style={{ padding: '3px 8px', borderRadius: 3, border: '1px solid var(--border)', background: 'white', fontSize: '0.66rem', cursor: 'pointer' }}
                >
                  Tühi
                </button>
              </div>
            </div>
            <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
              {onlineParks.map(p => {
                const active = selectedSlots.includes(p.slot);
                return (
                  <button
                    key={p.slot}
                    onClick={() => toggleSlot(p.slot)}
                    style={{
                      padding: '4px 10px', borderRadius: 3,
                      border: `1px solid ${active ? '#18181b' : 'var(--border)'}`,
                      background: active ? '#18181b' : 'white',
                      color: active ? 'white' : (p.online ? 'var(--text-2)' : 'var(--text-3)'),
                      fontFamily: 'inherit', fontSize: '0.7rem', fontWeight: 500,
                      cursor: 'pointer', opacity: p.online ? 1 : 0.6,
                    }}
                  >
                    #{p.slot} {p.name}{!p.online && ' ·off'}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Periood */}
          <div style={{ flex: '1 1 100%' }}>
            <div style={{ fontSize: '0.62rem', color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 4 }}>
              Periood
            </div>
            <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', alignItems: 'center' }}>
              {PERIODS.filter(p => p.group === 'rolling').map(p => (
                <button key={p.id} onClick={() => setPeriod(p.id)}
                  style={{ padding: '5px 11px', borderRadius: 4,
                    border: period === p.id ? '1px solid #18181b' : '1px solid var(--border)',
                    background: period === p.id ? '#18181b' : 'white',
                    color: period === p.id ? 'white' : 'var(--text-2)',
                    fontSize: '0.7rem', cursor: 'pointer' }}>
                  {p.label}
                </button>
              ))}
              <span style={{ width: 1, height: 18, background: 'var(--border)', margin: '0 4px' }} />
              {PERIODS.filter(p => p.group === 'calendar').map(p => (
                <button key={p.id} onClick={() => setPeriod(p.id)}
                  style={{ padding: '5px 11px', borderRadius: 4,
                    border: period === p.id ? '1px solid #18181b' : '1px solid var(--border)',
                    background: period === p.id ? '#18181b' : 'white',
                    color: period === p.id ? 'white' : 'var(--text-2)',
                    fontSize: '0.7rem', cursor: 'pointer' }}>
                  {p.label}
                </button>
              ))}
              <span style={{ width: 1, height: 18, background: 'var(--border)', margin: '0 4px' }} />
              <button onClick={() => setPeriod('custom')}
                style={{ padding: '5px 11px', borderRadius: 4,
                  border: period === 'custom' ? '1px solid #18181b' : '1px solid var(--border)',
                  background: period === 'custom' ? '#18181b' : 'white',
                  color: period === 'custom' ? 'white' : 'var(--text-2)',
                  fontSize: '0.7rem', cursor: 'pointer' }}>
                📅 Vali ajavahemik
              </button>
            </div>
            {period === 'custom' && (
              <div style={{ display: 'flex', gap: 8, marginTop: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                <label style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <span style={{ fontSize: '0.6rem', color: 'var(--text-3)' }}>Algus</span>
                  <input type="datetime-local" value={customFrom} onChange={e => setCustomFrom(e.target.value)}
                    style={{ padding: '5px 8px', borderRadius: 4, border: '1px solid var(--border-strong)', fontSize: '0.75rem' }} />
                </label>
                <label style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <span style={{ fontSize: '0.6rem', color: 'var(--text-3)' }}>Lõpp</span>
                  <input type="datetime-local" value={customTo} onChange={e => setCustomTo(e.target.value)}
                    style={{ padding: '5px 8px', borderRadius: 4, border: '1px solid var(--border-strong)', fontSize: '0.75rem' }} />
                </label>
                {(!customFrom || !customTo) && (
                  <span style={{ fontSize: '0.7rem', color: '#dc2626', marginTop: 18 }}>⚠ Vali nii algus kui lõpp</span>
                )}
              </div>
            )}
          </div>

          {/* Näitajate valik */}
          <div style={{ flex: 1, minWidth: 300 }}>
            <div style={{ fontSize: '0.62rem', color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 4 }}>
              Näitajad
            </div>
            <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
              {METRICS.map(m => {
                const active = selectedMetrics.includes(m.id);
                return (
                  <button
                    key={m.id}
                    onClick={() => toggleMetric(m.id)}
                    style={{
                      padding: '5px 11px', borderRadius: 4,
                      border: `1px solid ${active ? m.color : 'var(--border)'}`,
                      background: active ? m.color + '20' : 'white',
                      color: active ? m.color : 'var(--text-2)',
                      fontFamily: 'inherit', fontSize: '0.7rem', fontWeight: 500,
                      cursor: 'pointer',
                    }}
                  >
                    {m.label}
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        {/* Status */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12, fontSize: '0.7rem', color: 'var(--text-3)' }}>
          <div>
            {selectedPark ? (
              <span><strong style={{ color: 'var(--text)' }}>{selectedPark.name}</strong> · {snapshots.length} punkti</span>
            ) : (
              <span>Pole parki valitud</span>
            )}
          </div>
          {loading && <span>Laadin...</span>}
          {error && <span style={{ color: 'var(--negative)' }}>Viga: {error}</span>}
        </div>

        {/* Graafik */}
        {snapshots.length === 0 && !loading && !error && (
          <div style={{
            padding: 40, textAlign: 'center', color: 'var(--text-3)',
            background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 6,
          }}>
            Andmed selle perioodi kohta puuduvad.<br/>
            <span style={{ fontSize: '0.7rem' }}>Snapshot'id salvestatakse iga 5 minuti tagant alates server'i käivitamisest.</span>
          </div>
        )}

        {snapshots.length > 0 && visibleMetrics.length > 0 && (
          <div style={{
            background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 6,
            padding: 16, height: 480,
          }}>
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={chartData} margin={{ top: 5, right: 30, left: 10, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f1f4" />
                <XAxis dataKey="time" stroke="#71717a" fontSize={11} />
                <YAxis
                  stroke="#71717a" fontSize={11}
                  domain={[
                    (dataMin) => dataMin - Math.max(0.05 * Math.abs(dataMin || 1), 1),
                    (dataMax) => dataMax + Math.max(0.05 * Math.abs(dataMax || 1), 1),
                  ]}
                  allowDecimals
                  width={70}
                  tickFormatter={(v) => {
                    if (v == null) return '';
                    const abs = Math.abs(v);
                    if (abs >= 1e9) return (v / 1e9).toFixed(2) + ' G';
                    if (abs >= 1e6) return (v / 1e6).toFixed(2) + ' M';
                    if (abs >= 1e3) return (v / 1e3).toFixed(1) + ' k';
                    return v.toLocaleString('et-EE', { maximumFractionDigits: 2 });
                  }}
                />
                <Tooltip
                  contentStyle={{ background: 'white', border: '1px solid var(--border-strong)', borderRadius: 4, fontSize: '0.75rem' }}
                  labelFormatter={(label) => `Aeg: ${label}`}
                  formatter={(value, name) => {
                    const m = METRICS.find(x => x.label === name);
                    return [value !== null ? `${fmt(value, 2)} ${m?.unit || ''}` : '—', name];
                  }}
                />
                <Legend wrapperStyle={{ fontSize: '0.72rem' }} />
                {visibleMetrics.map(m => (
                  <Line
                    key={m.id}
                    type="monotone"
                    dataKey={m.id}
                    name={m.label}
                    stroke={m.color}
                    strokeWidth={1.5}
                    dot={false}
                    isAnimationActive={false}
                    connectNulls
                  />
                ))}
              </LineChart>
            </ResponsiveContainer>
          </div>
        )}

        {/* Energia kokkuvõte perioodi kohta — cumulative deltad */}
        {summary && (
          <div style={{ marginTop: 16 }}>
            <div style={{
              fontSize: '0.65rem', color: 'var(--text-3)', textTransform: 'uppercase',
              letterSpacing: '0.06em', marginBottom: 8, fontWeight: 500,
            }}>
              Energia kokkuvõte · {summary.duration_h.toFixed(1)} h
              {' · '}
              <span style={{ color: 'var(--text-4)', fontFamily: 'JetBrains Mono, monospace', fontSize: '0.95em', textTransform: 'none' }}>
                {fmtTime(summary.first_ts, period)} – {fmtTime(summary.last_ts, period)}
              </span>
            </div>
            {/* Energiavood: 4 põhinäidikut */}
            <div style={{
              display: 'grid', gap: 1,
              gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))',
              background: 'var(--border)', border: '1px solid var(--border)',
              borderRadius: 6, overflow: 'hidden',
            }}>
              <SummaryCell label="PEJ toodetud"     value={summary.pej_kwh}          color="#ca8a04" icon="☀" />
              <SummaryCell label="Võrku (eksport)"  value={summary.voork_export_kwh} color="#15803d" icon="↑" />
              <SummaryCell label="Võrgust (import)" value={summary.voork_import_kwh} color="#b91c1c" icon="↓" />
              <SummaryCell label="BAU AC kasutus"   value={summary.bau_ac_kwh}       color="#6d28d9" icon="⚡" />
            </div>

            {isAll && summary.pej_kwh != null && (
              <div style={{ marginTop: 8, fontSize: '0.65rem', color: 'var(--text-3)', fontStyle: 'italic' }}>
                Kõikide aktiivsete parkide summa.
              </div>
            )}
          </div>
        )}

        {/* Statistika kokkuvõte (min/avg/max) */}
        {snapshots.length > 0 && (
          <div style={{
            marginTop: 16, display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 1,
            background: 'var(--border)', border: '1px solid var(--border)',
            borderRadius: 6, overflow: 'hidden',
          }}>
            {visibleMetrics.map(m => {
              const values = chartData.map(d => d[m.id]).filter(v => v !== null && v !== undefined);
              if (values.length === 0) return null;
              const min = Math.min(...values);
              const max = Math.max(...values);
              const avg = values.reduce((a, b) => a + b, 0) / values.length;
              return (
                <div key={m.id} style={{ background: 'var(--surface)', padding: '10px 14px' }}>
                  <div style={{ fontSize: '0.6rem', color: m.color, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 4 }}>
                    {m.label}
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.7rem', fontFamily: 'JetBrains Mono, monospace' }}>
                    <span><span style={{ color: 'var(--text-3)' }}>min</span> {fmt(min, 1)}</span>
                    <span><span style={{ color: 'var(--text-3)' }}>avg</span> {fmt(avg, 1)}</span>
                    <span><span style={{ color: 'var(--text-3)' }}>max</span> {fmt(max, 1)}</span>
                  </div>
                </div>
              );
            })}
          </div>
        )}

      </div>
    </div>
  );
}

function SummaryCell({ label, value, color, icon, unitOverride }) {
  let display, unit;
  if (value == null) { display = '—'; unit = ''; }
  else if (unitOverride) {
    display = value.toLocaleString('et-EE', { maximumFractionDigits: unitOverride === '%' ? 1 : 2 });
    unit = unitOverride;
  } else if (value >= 1000) {
    display = (value / 1000).toLocaleString('et-EE', { maximumFractionDigits: 2 });
    unit = 'MWh';
  } else {
    display = value.toLocaleString('et-EE', { maximumFractionDigits: 1 });
    unit = 'kWh';
  }
  return (
    <div style={{ background: 'var(--surface)', padding: '14px 16px' }}>
      <div style={{
        fontSize: '0.62rem', color, textTransform: 'uppercase',
        letterSpacing: '0.06em', fontWeight: 500, marginBottom: 6,
        display: 'flex', alignItems: 'center', gap: 5,
      }}>
        <span style={{ fontSize: '0.95rem' }}>{icon}</span>
        {label}
      </div>
      <div style={{
        fontSize: '1.35rem', fontWeight: 600, letterSpacing: '-0.02em',
        color: 'var(--text)', fontVariantNumeric: 'tabular-nums',
        lineHeight: 1.1,
      }}>
        {display}
        <span style={{ fontSize: '0.6em', color: 'var(--text-3)', marginLeft: 4, fontWeight: 500 }}>
          {unit}
        </span>
      </div>
    </div>
  );
}

/**
 * EquipmentTable — agregeeritud "Seadmed ja materjalid" tabel.
 *
 * Loendab parks.json _meta'st kõik installitud seadmed ja kuvab tabelina:
 *   # | NIMETUS | KOGUS | ÜHIK
 *
 * Hetkel ilma hinnata — kui hinna-info parks.json'i lisatakse, lisanduvad
 * Ühiku hind + Summa veerud automaatselt.
 */
export function EquipmentTable({ parks }) {
  if (!parks || parks.length === 0) return null;

  // Loendame seadmed kategooriate kaupa
  const counts = new Map();   // key → { name, qty, unit, model? }
  const add = (key, fields) => {
    if (counts.has(key)) counts.get(key).qty += (fields.qty || 1);
    else counts.set(key, { qty: 0, ...fields });
    counts.get(key).qty += 0;  // ensure exists
  };

  for (const p of parks) {
    if (!p.name) continue;
    const m = p._meta || {};
    // Aku
    if (m.battery_capacity_kwh) {
      const key = `bat_${m.battery_capacity_kwh}`;
      add(key, {
        name: `Lithtech ${m.battery_capacity_kwh} kWh akukapp`,
        model: 'LTC0832314L',
        unit: 'tk', qty: 1, group: 'Aku',
      });
    }
    // PCS
    if (m.pcs_model) {
      const key = `pcs_${m.pcs_model}`;
      add(key, {
        name: `IN-POWER PCS ${m.battery_power_kw || 125} kW`,
        model: m.pcs_model,
        unit: 'tk', qty: 1, group: 'PCS',
      });
    }
    // Inverterid
    for (const inv of (p.inverters || [])) {
      const key = `inv_${inv.type}`;
      add(key, {
        name: inv.type.startsWith('GW') ? `Goodwe ${inv.type}` : `Huawei ${inv.type}`,
        model: inv.type,
        unit: 'tk', qty: 1, group: 'Inverter',
      });
    }
    // ex9EMS arvestid — 2 tk pargi kohta (PV + GRID)
    add('meter_ex9ems', {
      name: 'NOARK ex9EMS 3P 4M mõõdik',
      model: 'ex9EMS', unit: 'tk', qty: 2, group: 'Mõõdikud',
    });
  }

  // Konverteeri grupeerituks sorteerimiseks
  const items = [...counts.values()].sort((a, b) => {
    const order = { 'Aku': 1, 'PCS': 2, 'Inverter': 3, 'Mõõdikud': 4 };
    return (order[a.group] || 99) - (order[b.group] || 99);
  });

  return (
    <div style={{
      marginTop: 24,
      background: 'var(--surface)',
      border: '1px solid var(--border)',
      borderRadius: 6,
      overflow: 'hidden',
    }}>
      <div style={{
        display: 'flex', alignItems: 'center', gap: 8,
        padding: '12px 16px',
        background: '#16a34a', color: 'white',
        fontSize: '0.95rem', fontWeight: 600,
      }}>
        <span style={{ fontSize: 18 }}>⬡</span>
        Seadmed ja materjalid
      </div>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.82rem' }}>
        <thead>
          <tr style={{ background: 'linear-gradient(180deg, #15803d, #166534)', color: 'white' }}>
            <th style={cellH}>#</th>
            <th style={{ ...cellH, textAlign: 'left' }}>NIMETUS</th>
            <th style={{ ...cellH, textAlign: 'left' }}>MUDEL</th>
            <th style={{ ...cellH, textAlign: 'right' }}>KOGUS</th>
            <th style={cellH}>ÜHIK</th>
          </tr>
        </thead>
        <tbody>
          {items.map((it, idx) => (
            <tr key={idx} style={{
              background: idx % 2 ? '#f9fafb' : 'white',
              borderTop: '1px solid #e5e7eb',
            }}>
              <td style={{ ...cell, fontWeight: 600 }}>{idx + 1}</td>
              <td style={{ ...cell, textAlign: 'left', fontWeight: 600 }}>{it.name}</td>
              <td style={{ ...cell, textAlign: 'left', color: '#64748b', fontFamily: 'JetBrains Mono, monospace', fontSize: '0.74rem' }}>
                {it.model || '—'}
              </td>
              <td style={{ ...cell, textAlign: 'right', fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>{it.qty}</td>
              <td style={cell}>{it.unit}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

const cellH = {
  padding: '12px 16px',
  fontSize: '0.7rem', fontWeight: 600,
  textTransform: 'uppercase', letterSpacing: '0.05em',
  textAlign: 'center',
  borderBottom: 'none',
};
const cell = {
  padding: '14px 16px',
  textAlign: 'center',
  color: '#0f172a',
};

export default HistoryPage;
